import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, forkJoin, map, of, switchMap } from 'rxjs';

export interface SeoData {
  title?: string;
  description?: string;
  ogImage?: string;
  lcpImage?: string;
  lcpImageSrcSet?: string;
  lcpImageSizes?: string;
  lcpImageType?: string;
}

const OG_TAGS = ['og:title', 'og:description', 'og:url', 'og:type', 'og:image'] as const;
const TWITTER_TAGS = ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'] as const;
const LCP_PRELOAD_SELECTOR = 'link[data-sr-lcp-preload="true"]';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getDeepestRoute(this.activatedRoute)),
        switchMap((route) => route.data),
        switchMap((data) => {
          const seo: SeoData | undefined = data['seo'];
          if (!seo) {
            return of({ seo: undefined, resolvedTitle: '', resolvedDescription: '' });
          }
          return forkJoin({
            resolvedTitle: seo.title ? this.translate.get(seo.title) : of(''),
            resolvedDescription: seo.description ? this.translate.get(seo.description) : of(''),
          }).pipe(map((resolved) => ({ seo, ...resolved })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ seo, resolvedTitle, resolvedDescription }) => {
        this.updateTags(seo, resolvedTitle, resolvedDescription);
      });
  }

  private updateTags(seo: SeoData | undefined, resolvedTitle: string, resolvedDescription: string): void {
    if (!seo) {
      this.clearSeoTags();
      return;
    }

    const canonicalUrl = this.getCanonicalUrl();

    if (resolvedTitle) {
      this.title.setTitle(resolvedTitle);
    }

    if (resolvedDescription) {
      this.meta.updateTag({ name: 'description', content: resolvedDescription });
    } else {
      this.meta.removeTag('name="description"');
    }

    this.meta.updateTag({ property: 'og:title', content: resolvedTitle });
    this.meta.updateTag({ property: 'og:description', content: resolvedDescription });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    if (seo.ogImage) {
      this.meta.updateTag({ property: 'og:image', content: seo.ogImage });
    } else {
      this.meta.removeTag('property="og:image"');
    }

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: resolvedTitle });
    this.meta.updateTag({ name: 'twitter:description', content: resolvedDescription });

    if (seo.ogImage) {
      this.meta.updateTag({ name: 'twitter:image', content: seo.ogImage });
    } else {
      this.meta.removeTag('name="twitter:image"');
    }

    this.updateLcpPreload(seo);
  }

  private clearSeoTags(): void {
    this.meta.removeTag('name="description"');
    for (const tag of OG_TAGS) {
      this.meta.removeTag(`property="${tag}"`);
    }
    for (const tag of TWITTER_TAGS) {
      this.meta.removeTag(`name="${tag}"`);
    }

    this.document.head.querySelector(LCP_PRELOAD_SELECTOR)?.remove();
  }

  private getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private getCanonicalUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.origin + this.router.url.split('?')[0];
    }
    return this.router.url.split('?')[0];
  }

  private updateLcpPreload(seo: SeoData): void {
    const existingLink = this.document.head.querySelector<HTMLLinkElement>(LCP_PRELOAD_SELECTOR);

    if (!seo.lcpImage) {
      existingLink?.remove();
      return;
    }

    const preloadLink = existingLink ?? this.document.createElement('link');

    preloadLink.setAttribute('data-sr-lcp-preload', 'true');
    preloadLink.setAttribute('rel', 'preload');
    preloadLink.setAttribute('as', 'image');
    preloadLink.setAttribute('href', seo.lcpImage);
    preloadLink.setAttribute('fetchpriority', 'high');

    if (seo.lcpImageType) {
      preloadLink.setAttribute('type', seo.lcpImageType);
    } else {
      preloadLink.removeAttribute('type');
    }

    if (seo.lcpImageSrcSet) {
      preloadLink.setAttribute('imagesrcset', seo.lcpImageSrcSet);
    } else {
      preloadLink.removeAttribute('imagesrcset');
    }

    if (seo.lcpImageSizes) {
      preloadLink.setAttribute('imagesizes', seo.lcpImageSizes);
    } else {
      preloadLink.removeAttribute('imagesizes');
    }

    if (!existingLink) {
      this.document.head.appendChild(preloadLink);
    }
  }
}
